import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, GraduationCapIcon, LoaderIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { readContactParams, readMatchParams, readProfileParams } from '../utils/matchParams';
import { sanitizeTcpaHtml } from '../utils/sanitizeHtml';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ACCENT, SelectField, StageProgress, StepCard } from '../components/form/FormStepUI';
import { TCPADisclosure } from '../components/TCPADisclosure';
import { TrackingNotice } from '../components/TrackingNotice';
import { trackLeadConversion } from '../utils/conversion';
import { areaFromProgram, clicksSearch, leadsSearch, tcpaPost, tcpaSearch, type ContactProfile, type MMSchool, type SearchProfile, type TCPAResult } from '../services/mediaMatchers';
// Step option lists
// Labels match the partner's value mapping, so the codes sent are unambiguous.
const educationLevels = ['High School Diploma / GED', 'Some High School', 'Some College', "Associate's Degree", "Bachelor's Degree", "Master's Degree", 'Doctorate'];
function getGradYearOptions(): string[] {
  const currentYear = new Date().getFullYear();
  const years: string[] = [];
  // Anything older than 60 years is rejected by the partner, so the list stops
  // there and collapses into a single "Before 1965" option.
  for (let y = currentYear + 2; y >= currentYear - 60; y--) {
    years.push(String(y));
  }
  years.push('Before 1965');
  return years;
}
const gradYearOptions = getGradYearOptions();
const startTimeframes = ['Immediately', '1–3 months', '3–6 months', '6–12 months', 'More than a year', 'Undecided'];
export function RequestInformationForm() {
  usePageMeta('Request Information | Find Online Degree Programs', 'Complete a quick form to get matched with accredited online degree programs.');
  const navigate = useNavigate();
  const location = useLocation();
  // Everything the previous step already knew about this visitor: the program
  // selections, plus any profile answers and contact details carried in from a
  // campaign link or a /get-info/ email capture.
  const navState = (location.state || {}) as {
    degreeInterest?: string;
    areaOfStudy?: string;
    specialization?: string;
    currentEducation?: string;
    gradYear?: string;
    startDate?: string;
    isMilitary?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
  // Fall back to URL params so a direct anchor link lands here pre-filled.
  const urlPrefill = readMatchParams();
  const profilePrefill = readProfileParams();
  const contactPrefill = readContactParams();
  const prefill = {
    degreeInterest: navState.degreeInterest || urlPrefill.degreeInterest,
    // The area is derivable from the program id when it isn't given.
    areaOfStudy: navState.areaOfStudy || urlPrefill.areaOfStudy || areaFromProgram(urlPrefill.specialization),
    specialization: navState.specialization || urlPrefill.specialization,
    // URL params first, then nav state wins where both are present.
    ...profilePrefill,
    ...contactPrefill,
    ...(navState.currentEducation ? {
      currentEducation: navState.currentEducation
    } : {}),
    ...(navState.gradYear ? {
      gradYear: navState.gradYear
    } : {}),
    ...(navState.startDate ? {
      startDate: navState.startDate
    } : {}),
    ...(navState.isMilitary ? {
      isMilitary: navState.isMilitary
    } : {}),
    ...(navState.firstName ? {
      firstName: navState.firstName
    } : {}),
    ...(navState.lastName ? {
      lastName: navState.lastName
    } : {}),
    ...(navState.email ? {
      email: navState.email
    } : {}),
    ...(navState.phone ? {
      phone: formatPhone(navState.phone)
    } : {})
  };
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep3
  } = useFormState(prefill);
  const [step, setStep] = useState(1);
  const [zipLoading, setZipLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stepError, setStepError] = useState<string>('');
  // Mounts the LeadiD and TrustedForm scripts, so their hidden inputs are
  // populated before any Media Matchers call fires.
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();

  // --- Media Matchers state -------------------------------------------------
  /** The PII step. The searches must resolve before it renders. */
  const CONTACT_STEP = 5;
  /** Location — the last step that contributes to the search payload. */
  const LOCATION_STEP = 4;
  const [leads, setLeads] = useState<MMSchool[] | null>(null);
  const [tcpa, setTcpa] = useState<TCPAResult | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [tcpaFailed, setTcpaFailed] = useState(false);
  const searchFired = useRef(false);
  const buildProfile = useCallback((): SearchProfile => ({
    degreeInterest: formData.degreeInterest,
    areaStudy: formData.areaOfStudy,
    mProgram: formData.specialization,
    currentEducation: formData.currentEducation,
    gradYear: formData.gradYear,
    startTimeframe: formData.startDate,
    militaryAffiliation: formData.isMilitary,
    zipCode: formData.zipCode.replace(/\D/g, ''),
    address: formData.address,
    city: formData.city,
    state: formData.state
  }), [formData]);

  /**
   * The in-flight search, so submitting can wait on the actual response rather
   * than a guessed delay. Usually it has already resolved by the time the
   * visitor finishes typing their contact details.
   */
  const searchPromise = useRef<Promise<MMSchool[] | null> | null>(null);
  const runSearches = useCallback(async () => {
    setSearchLoading(true);
    setTcpaFailed(false);
    const profile = buildProfile();
    // Both fire in parallel — neither depends on the other.
    const leadsRequest = leadsSearch(profile);
    searchPromise.current = leadsRequest;
    const [leadsResult, tcpaResult] = await Promise.all([leadsRequest, tcpaSearch(profile)]);
    setLeads(leadsResult || []);
    setTcpa(tcpaResult);
    if (!tcpaResult) setTcpaFailed(true);
    setSearchLoading(false);
  }, [buildProfile]);

  // Safety net: if the visitor reaches the PII step without the searches
  // having run (a restored session, say), fire them on arrival.
  useEffect(() => {
    if (step !== CONTACT_STEP || searchFired.current) return;
    searchFired.current = true;
    runSearches();
  }, [step, runSearches]);
  const retrySearches = () => {
    searchFired.current = true;
    runSearches();
  };
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
        }
      }
    } catch {
      /* ignore */
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const goBack = () => {
    setStepError('');
    if (step === 1) {
      navigate('/get-matched');
    } else {
      setStep(step - 1);
    }
  };
  const goNext = () => {
    setStepError('');
    // Per-step validation
    if (step === 1 && !formData.currentEducation) {
      setStepError('Please select your highest level of education.');
      return;
    }
    if (step === 2 && !formData.gradYear) {
      setStepError('Please select your graduation year.');
      return;
    }
    if (step === 3 && !formData.startDate) {
      setStepError('Please select when you would like to start.');
      return;
    }
    if (step === LOCATION_STEP) {
      if (!formData.address.trim()) {
        setStepError('Please enter your street address.');
        return;
      }
      if (!formData.zipCode.trim() || formData.zipCode.replace(/\D/g, '').length < 5) {
        setStepError('Please enter a valid 5-digit ZIP code.');
        return;
      }
      // Location is the last input the search payload needs, so both Searches
      // fire here — before the PII step renders — and the TCPA disclaimer is
      // already in hand when the consent checkbox appears.
      if (!searchFired.current) {
        searchFired.current = true;
        runSearches();
      }
    }
    setStep(step + 1);
  };
  const handleSubmit = async () => {
    setStepError('');
    if (!validateUniStep3()) return;
    // Without the TCPA record there is no consent to post against.
    if (!tcpa) {
      setTcpaFailed(true);
      setStepError('We could not load the consent details. Please retry before submitting.');
      return;
    }
    setIsSubmitting(true);
    const profile = buildProfile();
    const contact: ContactProfile = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      phone: stripPhone(formData.phone)
    };
    // Consent is posted first; the click search and the still-in-flight lead
    // search run alongside it. Submitting lasts exactly as long as the slowest
    // of these — no padded delay.
    const posted = await tcpaPost(tcpa.mRecordID, contact);
    // Conversion fires on confirmation, not on the click.
    if (posted) trackLeadConversion({
      source: 'tcpa'
    });
    const [clicks, resolvedLeads] = await Promise.all([clicksSearch(profile, contact, tcpa.mBrandID),
    // Already resolved in the common case; awaited so the carousel never
    // opens on an empty lead list that was merely still loading.
    leads ? Promise.resolve(leads) : searchPromise.current ?? Promise.resolve([])]);
    setIsSubmitting(false);
    navigate('/matches', {
      state: {
        leads: resolvedLeads || [],
        clicks: clicks || [],
        profile,
        contact,
        disclosure: tcpa.disclosure,
        excludeBrand: tcpa.mBrandID
      }
    });
  };
  // Card content for each step
  const renderStepCard = () => {
    if (step === 1) {
      return <StepCard title="What is your highest level of education?" onContinue={goNext} ctaLabel="Continue" info="Your education background helps schools understand where you are in your academic journey and recommend the right program level." error={stepError} data-id="element-2562" data-mp="lom7y78z0uh2">
          <SelectField value={formData.currentEducation} onChange={v => updateFormData('currentEducation', v)} options={educationLevels.map(l => ({
          label: l,
          value: l
        }))} data-id="element-2563" data-mp="31icuill86e42" />
        </StepCard>;
    }
    if (step === 2) {
      return <StepCard title="When did you graduate high school?" onContinue={goNext} ctaLabel="Continue" info="Knowing when you graduated helps us verify eligibility and connect you with programs that fit your timeline." error={stepError} data-id="element-2564" data-mp="2dlghv95ftv7s">
          <SelectField value={formData.gradYear} onChange={v => updateFormData('gradYear', v)} options={gradYearOptions.map(y => ({
          label: y,
          value: y
        }))} data-id="element-2565" data-mp="28llp1023qvk3" />
        </StepCard>;
    }
    if (step === 3) {
      return <StepCard title="When would you like to start classes?" onContinue={goNext} ctaLabel="Continue" info="Your preferred start date helps us find programs with upcoming enrollment windows that work for your schedule." error={stepError} data-id="element-2566" data-mp="3oztzhw9llefd">
          <SelectField value={formData.startDate} onChange={v => updateFormData('startDate', v)} options={startTimeframes.map(t => ({
          label: t,
          value: t
        }))} data-id="element-2567" data-mp="3nhvehiipkwfm" />
        </StepCard>;
    }
    if (step === 4) {
      return <StepCard title="Where are you located?" onContinue={goNext} ctaLabel="Continue" info="Your location helps us find programs available in your area and determine eligibility for state-specific financial aid." error={stepError} data-id="element-2568" data-mp="f2lgalhxwbd4">
          <div className="space-y-5" data-id="element-2569" data-mp="dfk76eef19g">
            <div data-id="element-2570" data-mp="ce58qu9humg">
              <label htmlFor="address" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-2571" data-mp="xtcohjq6xvpx">
                Street Address{' '}
                <span style={{
                color: ACCENT
              }} data-id="element-2572" data-mp="t132ufw3zhtg">
                  *
                </span>
              </label>
              <input id="address" type="text" value={formData.address} onChange={e => updateFormData('address', e.target.value)} placeholder="123 Main St" className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-0" style={{
              '--tw-ring-color': ACCENT
            } as React.CSSProperties} data-id="element-2573" data-mp="15f84999uq3vm" />
            </div>

            <div className="bg-warm border border-cta-light rounded-lg p-4" data-id="element-2574" data-mp="1y0hsmc4phc94">
              <label htmlFor="zipCode" className="flex items-center gap-2 text-sm font-bold text-gray-800 mb-2" data-id="element-2575" data-mp="231ozm8z0y7eq">
                <span data-id="element-2576" data-mp="273ahk8qf1bvs">📍</span>
                Enter your ZIP Code
              </label>
              <div className="relative" data-id="element-2577" data-mp="34qc2le9zsrv4">
                <input id="zipCode" type="text" value={formData.zipCode} onChange={e => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                updateFormData('zipCode', val);
                if (val.length === 5) lookupZip(val);
              }} placeholder="e.g. 90210" maxLength={5} className="block w-full px-4 py-3 text-base border-2 rounded-md bg-white focus:outline-none" style={{
                borderColor: ACCENT
              }} data-id="element-2578" data-mp="34p9bp6co6pf7" />
                {zipLoading && <div className="absolute right-3 top-1/2 -translate-y-1/2" data-id="element-2579" data-mp="2gtyad7lnnio2">
                    <LoaderIcon className="w-4 h-4 animate-spin text-gray-500" data-id="element-2580" data-mp="2ut6co9d476wh" />
                  </div>}
              </div>
              {formData.city && formData.state && <p className="text-xs text-gray-600 mt-2" data-id="element-2581" data-mp="2ptbi9vu4rz6o">
                  📌 {formData.city}, {formData.state}
                </p>}
            </div>
          </div>
        </StepCard>;
    }
    if (step === 5) {
      const locationLine = formData.city && formData.state ? `${formData.city}, ${formData.state}` : '';
      return <StepCard title="Last step! Where should we send your matches?" subtitle={`We'll match you with top programs in ${locationLine}.`} onContinue={handleSubmit} ctaLabel={isSubmitting ? 'Submitting...' : 'See Matching Colleges'} ctaIcon={<GraduationCapIcon className="w-5 h-5 ml-2 inline-block" data-id="element-2583" data-mp="wbme3jb8mqrv" />} isSubmitting={isSubmitting || searchLoading} error={stepError} data-id="element-2582" data-mp="hmzq6t7joqp1">
          <div className="space-y-5" data-id="element-2584" data-mp="119nutxl6ye06">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2585" data-mp="mluf8cw3vbhl">
              <div data-id="element-2586" data-mp="mjgm49kzk9co">
                <label htmlFor="firstName" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-2587" data-mp="rknv2byv7ekx">
                  First Name{' '}
                  <span style={{
                  color: ACCENT
                }} data-id="element-2588" data-mp="1f8f6ldckznl5">
                    *
                  </span>
                </label>
                <input id="firstName" type="text" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} placeholder="John" className={`block w-full px-4 py-3 text-base border rounded-md focus:outline-none focus:ring-2 ${errors.firstName ? 'border-red-400' : 'border-gray-300'}`} style={{
                '--tw-ring-color': ACCENT
              } as React.CSSProperties} data-id="element-2589" data-mp="1oxuje7atw37e" />
                {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-2590" data-mp="wa82xygi5434">
                    {errors.firstName}
                  </p>}
              </div>
              <div data-id="element-2591" data-mp="n8id2x29qd7g">
                <label htmlFor="lastName" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-2592" data-mp="n7ywbjoctuv0">
                  Last Name{' '}
                  <span style={{
                  color: ACCENT
                }} data-id="element-2593" data-mp="33uopuy969tsg">
                    *
                  </span>
                </label>
                <input id="lastName" type="text" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} placeholder="Doe" className={`block w-full px-4 py-3 text-base border rounded-md focus:outline-none focus:ring-2 ${errors.lastName ? 'border-red-400' : 'border-gray-300'}`} style={{
                '--tw-ring-color': ACCENT
              } as React.CSSProperties} data-id="element-2594" data-mp="3cvd4nv6e3y6e" />
                {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-2595" data-mp="21f59wfx79r5a">{errors.lastName}</p>}
              </div>
            </div>

            <div data-id="element-2596" data-mp="uv8dyvk5cx6g">
              <label htmlFor="email" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-2597" data-mp="pu16kxlx200b">
                Email Address{' '}
                <span style={{
                color: ACCENT
              }} data-id="element-2598" data-mp="13rprg5fzh75d">
                  *
                </span>
              </label>
              <input id="email" type="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} placeholder="john@example.com" className={`block w-full px-4 py-3 text-base border rounded-md focus:outline-none focus:ring-2 ${errors.email ? 'border-red-400' : 'border-gray-300'}`} style={{
              '--tw-ring-color': ACCENT
            } as React.CSSProperties} data-id="element-2599" data-mp="1dq5iy9ixaife" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-2600" data-mp="2b33pbyjwpbjt">{errors.email}</p>}
            </div>

            <div data-id="element-2601" data-mp="2g1x3zuaxj8lk">
              <label htmlFor="phone" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-2602" data-mp="224s1u5ec6tvl">
                Phone Number{' '}
                <span style={{
                color: ACCENT
              }} data-id="element-2603" data-mp="267y5r6dz88yw">
                  *
                </span>
              </label>
              <input id="phone" type="tel" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} placeholder="555-123-4567" maxLength={13} className={`block w-full px-4 py-3 text-base border rounded-md focus:outline-none focus:ring-2 ${errors.phone ? 'border-red-400' : 'border-gray-300'}`} style={{
              '--tw-ring-color': ACCENT
            } as React.CSSProperties} data-id="element-2604" data-mp="1x7i5e5sj5hy9" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-2605" data-mp="13mx98qhjr0io">{errors.phone}</p>}
            </div>

            {/* Consent language comes from the TCPA Search response, so the
                text shown is the text the certificate records. */}
            {searchLoading ? <div className="flex items-center gap-2 text-sm text-gray-500 pt-2" data-id="element-2606" data-mp="20ak4wm6sqev2">
                <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-2607" data-mp="1ml03pksv5pnv" />
                Loading consent details...
              </div> : tcpaFailed ? <div className="rounded-lg border border-red-200 bg-red-50 p-4" data-id="element-2608" data-mp="1rgynsw75slih">
                <p className="text-sm text-red-800 font-medium" data-id="element-2609" data-mp="2f7kv2l9ca1rf">
                  We couldn't load the consent details.
                </p>
                <p className="text-xs text-red-600 mt-1" data-id="element-2610" data-mp="2te63ea1j69ga">
                  This is required before we can submit your request.
                </p>
                <button type="button" onClick={retrySearches} className="mt-3 inline-flex items-center px-4 py-2 rounded-md bg-white border border-red-300 text-sm font-bold text-red-700 hover:bg-red-100 transition-colors duration-200" data-id="element-2611" data-mp="2pcb1p4fotu1z">
                  Retry
                </button>
              </div> : <TCPADisclosure checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} checkboxClassName="mt-1 h-4 w-4 rounded border-gray-300 cursor-pointer flex-shrink-0 accent-[#5B4EA8]" data-id="element-2612" data-mp="26f9sf7f3p7s8">
                {/* tcpaDisclaimer is partner-supplied HTML, so it is
                    sanitized rather than rendered as plain text. */}
                {tcpa?.disclosure ? <span dangerouslySetInnerHTML={{
              __html: sanitizeTcpaHtml(tcpa.disclosure)
            }} data-id="element-2613" data-mp="2ou18xvpggz2" /> : `I acknowledge that, by clicking the checkbox as my official
                signature, I consent to representatives of FindOnlineColleges.org and
                our Partner Schools contacting me about educational opportunities
                via email, text, or phone number(s), including through the use of
                an automatic dialer, at the number and email I provided. Message
                and data rates may apply. I understand that my consent is not a
                requirement to enroll, and I may withdraw my consent at any time.`}
              </TCPADisclosure>}
          </div>
        </StepCard>;
    }
    return null;
  };
  return <div className="min-h-screen flex flex-col bg-gray-50" data-id="element-2614" data-mp="31d0yqu7gip2t">
      <Header data-id="element-2615" data-mp="2tujmig874w65" />
      {/* min-h keeps the questionnaire filling the viewport on the short steps,
          so the footer always starts below the fold instead of competing with
          the answer options. */}
      <main className="flex-1 min-h-[85vh] py-6 sm:py-10 pb-24 sm:pb-32 px-4" data-id="element-2616" data-mp="2jw3ri261t2rd">
        <div className="max-w-3xl mx-auto" data-id="element-2617" data-mp="2ez3lfr978009">
          {/* Stage progress. Step 1 of the funnel is program selection on the
              start page, so the questionnaire begins at step 2. */}
          <StageProgress currentStep={step + 1} data-id="element-2618" data-mp="265ql448t1w45" />

          {/* Back button */}
          <div className="mt-2 mb-4" data-id="element-2619" data-mp="216nq7cmjh69w">
            <button type="button" onClick={goBack} className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors" data-id="element-2620" data-mp="217p21jbdpwso">
              <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-2621" data-mp="1r7y75giwpfs2" />
              Back
            </button>
          </div>

          {/* Step card */}
          <AnimatePresence mode="wait" data-id="element-2622" data-mp="2by24dvbg10vf">
            <motion.div key={step} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.2
          }} data-id="element-2623" data-mp="2bz3h0483xvf9">
              {renderStepCard()}
            </motion.div>
          </AnimatePresence>

          <div className="mt-8" data-id="element-2624" data-mp="1y76q23i6drkg">
            <TrackingNotice data-id="element-2625" data-mp="1y4aw0q9g7n1a" />
          </div>
        </div>
      </main>
      <Footer data-id="element-2626" data-mp="1k872z0ab9kqe" />
    </div>;
}